import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, X, Sliders } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../context/CartContext';
import { CartNotification } from '../components/CartNotification';
import { CategoryBanners } from '../components/CategoryBanners';
import { ProductFilters, ProductFilterSidebar } from '../components/ProductFilters';
import { BrandFilter } from '../components/BrandFilter';
import { ProductGrid } from '../components/ProductGrid';
import { filterProducts, sortProducts, extractProductMetadata } from '../utils/productFilters';
import { fetchProductsLight, fetchProducts, preloadProducts, shouldRefreshCache } from '../services/products';
import type { Product, ProductVariant } from '../types/product';

const PRODUCT_CLASSES = [
  { id: 'complete-scooters', name: 'Tõuksid' },
  { id: 'decks', name: 'Tallad' },
  { id: 'bars', name: 'Lenksud' },
  { id: 'wheels', name: 'Rattad' },
  { id: 'forks', name: 'Kahvlid' },
  { id: 'clamps', name: 'Klambrid' },
  { id: 'bar-ends', name: 'Lenksuotsad' },
  { id: 'pegs', name: 'Peegid' },
  { id: 'grips', name: 'Käepidemed' },
  { id: 'griptape', name: 'Gripteibid' },
];

const CATEGORIES = ['Kesktase', 'Pro tase', 'Street', 'Park', 'Laste'];

// SEO content for different pages
const SEO_CONTENT = {
  '/toukeratas': {
    title: 'Tõukeratas | Trikitõuksid ja Varuosad Eestis | Tõuks.ee',
    h1Title: 'Tõukeratas — trikitõuksid ja varuosad kõigile tasemetele',
    description: 'Tõukeratas algajale, harrastajale või profisõitjale. Lai valik trikitõukseid ja varuosi: Blunt, Aztek, Drone, North, Lucky, Striker, CORE ja teised tippmargid. Tasuta saatmine.',
    shortDescription: 'Tõuks.ee on Eesti ainuke spetsialiseerunud triki tõukeratta pood — siin leiad nii komplektse tõukeratta algajale kui ka kõik tõukeratta varuosad (tallad, lenksud, rattad, kahvlid, klambrid) kogenud sõitjale. Meie tõukerattad on pärit maailma juhtivate brändide ametlikelt edasimüüjatelt: Blunt, Aztek, Drone, North, Lucky, Striker, CORE, Fuzion, Prime, Tilt, Root Industries ja Antics. Kõik trikitõukerattad on 100% originaalid. Tasuta kohaletoimetamine DPD, Omniva ja SmartPost pakiautomaatidesse.',
    longDescription: 'Tõukeratas on spordivarustus, mis sobib nii lastele alates 6. eluaastast kui ka täiskasvanud profisõitjatele. Triki tõukeratas ehk trikitõukeratas on ehitatud spetsiaalselt skateparkide ja tänavasõidu jaoks — tugevama raamiga, väiksemate kuid vastupidavamate ratastega ja mõeldud taluma hüppeid ning intensiivset kasutust. Tõukerattad meie poes jagunevad tasemete järgi: algaja, kesktaseme ja pro. Tõukeratta valimisel on kõige olulisem arvestada sõitja taset ja kehaehitust: lenks peaks ulatuma puusani, talla laius peaks sobima jalaga. Algajatele sobib komplektne tõukeratas (complete scooter), mis on tehases kokku pandud ja kohe sõitmiseks valmis. Kogenumad sõitjad eelistavad custom-ehitust — valides iga osa eraldi vastavalt oma eelistustele. Tõukeratta varuosad meie poes hõlmavad taldasid, lenksusid, rattaid, kahvleid, klambreid, käepidemeid, peegisid, gripteipe ja muud. Tõukeratta hind meie poes algab entry-level mudelitest kuni top-taseme profivahendini — igaühele sobiv valik.'
  },
  '/toukeratas/lenks': {
    title: 'Tõukeratta lenks | Trikitõuski Lenksud ja Varuosad | Tõuks.ee',
    h1Title: 'Tõukeratta lenks',
    description: 'Lenksud tõukeratastele - erinevad materjalid, kujud ja suurused. T-bar ja Y-bar figuurid. Tuntumad brändid: Blunt, AZTEK, Tilt, Lucky ja Root Industries.',
    shortDescription: 'Lenks on tõukeratta üks olulisemaid osi, mis vastutab juhtimise, täpsuse ja raskuse eest. Meie veebipoes on saadaval lenksud kõigilt kuulsatelt tõuksibrändidelt nagu Blunt, Aztek, North ja Drone. Valida saate erinevatest materjalidest, suurustest ja värvidest. Õige lenks parandab teie juhtimisvõimet ja muudab sõidu mugavamaks.',
    longDescription: 'Lenks on trikitõuski üks kriitilisemaid komponente, mis otseselt mõjutab sõidu kogemust. Kvaliteetne lenks peab olema vastupidav, kerge ja ergonoomne. Meie sortiment sisaldab lenksusid erinevatest materjalidest - alumiiniumist, terasest ja muudest vastupidavatest materjalidest, mis tagavad maksimaalse tugevuse ja minimaalse kaalu. Iga lenks meie poest on hoolikalt testitud ja vastab kõrgeimale kvaliteedistandardile. Lenksud erinevad raskuse, pikkuse, laiuse ja kõverusega. Alustajad valivad sageli laiemad ja stabiilsemad lenksud, samas kogenumad sõitjad eelistavad kitsamaid ja kergemaid variante. Trikkide õppimine nõuab paremat kontrolli ja tasakaalu, seega on õige lenks väga oluline. Regulaarne hooldus tagab teie sõidu pikaajalise kestvuse, aeg-ajalt soovitame lenksu siiski väljavahetada.'
  },
  '/toukeratas/tald': {
    title: 'Tõukeratta tald | Trikitõuski Tallad ja Varuosad | Tõuks.ee',
    h1Title: 'Tõukeratta tald',
    description: 'Kerged ja vastupidavad tõukeratta tallad profisõitjatele ja algajatele. Lai valik eri materjalides ja suurustes. Brändid: Blunt, AZTEK, Striker, Lucky.',
    shortDescription: 'Tald on tõukeratta alus ja määrab sõidu stabiilsuse ning kontrolli. Meie veebipoes pakume laias valikut kõrge kvaliteediga taldasid kõigilt kuulsatelt tõuksibrändidelt. Tallad on saadaval erinevatest suurustest, kujudest ja materjalidest, mis sobivad eri sõitjate tasemetele. Õige tald annab teile vajaliku stabiilsuse ja jõudluse, et omandada uusi trikke.',
    longDescription: 'Tald on tõukeratta alus ja määrab kogu sõidu omadused. Kõrge kvaliteediga tald peab olema vastupidav, kerge ja stabiilne. Meie sortiment sisaldab taldasid erinevatest materjalidest nagu alumiiniumist, teras ja teised, mis tagavad maksimaalset jõudlust. Iga tald meie poest on hoolikalt valitud ja testitud. Tallad erinevad kõrguse, laiuse ja kuju poolest, mis mõjutavad sõidu käitumist ja kontrolli. Alustajad vajavad stabiilsemaid ja raskemaid taldasid ning kogenumad sõitjad võivad eelistada kõrgemaid taldasid paremate trikkide jaoks. Trikitõuksi tallad on tavaliselt väga kõrge vastupidavusega, et taluda tugevaid lööke ja kukkumisi. Meie tallad on saadaval erinevatest brändidest - Blunt, Aztek, North, Drone. Regulaarne kontrollimine ja vajadusel talla väljavahetamine tagavad teie varustusel pika eluea ja ohutuse.'
  },
  '/toukeratas/brandid': {
    title: 'Tõukeratta Brändid | Aztek, Blunt, Drone, North, Prey | Tõuks.ee',
    h1Title: 'Tõukeratta brändid',
    description: 'Siit leiad maailma kõige tuntumad tõukeratta brändid - Blunt, AZTEK, Indo, Lucky ja paljud teised. Erilised tõuksid ja varuosad profisõitjatele ja algajatele.',
    shortDescription: 'Meie e-pood pakub kõiki maailma kuulsaid tõukeratta brände - Aztek, Blunt, Drone, North, Prey ja paljusid teisi. Iga bränd on tuntud oma ainulaadse disaini, kõrge kvaliteediga materjalide ja jõudluse poolest. Õige tõukeratta brändi valik määrab teie sõidu kogemuse ja varustuse vastupidavuse.',
    longDescription: 'Meie e-poes on esindatud kõige kuulsamad trikitõuksi brändid. Aztek on tuntud oma kõrge kvaliteediga osadest ja vastupidavusest. Blunt paistab silma innovaatiliste disainilahenduste poolest. Drone pakub suurepärast hinnakvaliteedi suhet. North on professionaalide lemmik bränd, mille jõudlus on parimal tasemel. Prey toodab suurepäraseid komponente ja komplektseid tõukse. Lucky brändid pakuvad head valikut sobiva hinnaga. Striker on tuntud vastupidava konstruktsiooni poolest. CORE ja Fuzion pakuvad kvaliteetseid osasid kõigile tasemetele. Prime ja Tilt on uuemad brändid, kuid samuti kogunud suure usalduse sõitjatelt. Root Industries ja Antics on paljude lemmikvalikud. Õige brändi valik sõltub teie tasemest, eelarvest ja sõidustiilist. Professionaalsed sõitjad valivad tavaliselt North ja Aztek brände nende paremuse tõttu. Algajad võivad valida Drone või Lucky brände. Meie poest leiate complete tõukse, eraldi taldasid, lenksusid ja varuosasid.'
  },
  '/toukeratas/rattad': {
    title: 'Tõukeratta rattad | 100mm, 110mm, 120mm rattad | Tõuks.ee',
    h1Title: 'Tõukeratta rattad',
    description: 'Tõukeratta rattad 100mm, 110mm ja 120mm — alumiinium- ja nailonsüdamikuga. Blunt, Aztek, Root Industries. Lai valik eri värvidega. Tasuta saatmine Eestis.',
    shortDescription: 'Ratas on tõukeratta ainus kontakt asfaldiga — see mõjutab otseselt sõidu sujuvust, kiirust ja trikkide täpsust. Meie valikus on tõukeratta rattad 100mm, 110mm ja 120mm suurustes, alumiinium- ja nailonsüdamikuga, kõigilt juhtivalt brändilt: Blunt, Aztek, Root Industries, Lucky, Drone ja teised. Tasuta kohaletoimetamine DPD, Omniva ja SmartPost pakiautomaatidesse.',
    longDescription: 'Tõukeratta rattaid on kahte põhitüüpi: alumiiniumsüdamikuga rattad on vastupidavamad, kergemad ja mõeldud pro-taseme kasutuseks; nailonsüdamikuga rattad on odavamad ja sobivad algajatele. Rattasuurus mõjutab sõidu iseloomu: 100mm rattad on kergemad ja kiiremad pööretes, sobivad park-sõitjatele; 110mm on universaalne valik — hea kiirendus ja stabiilsus; 120mm rattad annavad suurema kiiruse ja sujuvama sõidu tänaval ning pikematel rampidel. Tõukeratta rattaid tuleks vahetada siis, kui uretaankiht on kulunud südamikuni, ratas on pragunenud, või sõit muutub ebaühtlaseks ja mürarikkaks. Enamik rattaid tulevad ABEC-7 või ABEC-9 laagritega — kontrolli alati toote kirjeldusest. Kõik meie rattad on 100% originaalid ametlikelt edasimüüjatelt.'
  },
  '/toukeratas/kahvlid': {
    title: 'Tõukeratta kahvel | IHC, HIC, SCS kahvlid | Tõuks.ee',
    h1Title: 'Tõukeratta kahvel',
    description: 'Tõukeratta kahvlid IHC, HIC ja SCS kompressiooniga. Alumiinium ja teras, kõik suurused. Blunt, Aztek, Drone, Root Industries. Tasuta saatmine Eestis.',
    shortDescription: 'Kahvel ühendab tõukeratta esiratta raamiga ning mõjutab otseselt sõidu täpsust, juhitavust ja stabilisatsiooni. Meie valikus on tõukeratta kahvlid IHC, HIC ja SCS kompressioonisüsteemidega — alumiiniumist ja terasest. Juhtivad brändid: Blunt, Aztek, Drone, Root Industries. Tasuta saatmine kogu Eestis.',
    longDescription: 'Tõukeratta kahvel on raami ja esiratta vaheline element, mis vastutab juhitavuse ja kompressiooni eest. Kahvlid jagunevad kompressioonisüsteemi järgi: IHC kahvlid (Integrated Headset Compression) on kergemad, mõeldud park-sõitjatele; HIC kahvlid (Hidden Internal Compression) on tugevamad, sobivad street-sõidule; SCS kahvlid (Standard Compression) on kõige tugevamad, mõeldud intensiivsemaks kasutuseks. Kahvli materjal mõjutab samuti: alumiiniumkahvel on kergem ja sobib õhutrikke jaoks, teraskahvel on vastupidavam tänavasõiduks. Enne kahvli ostmist kontrolli kolme asja: millise kompressioonisüsteemiga su lenks töötab; millist rattasuurust su tald mahutab; kas rattatelg on 8mm standardne. Kõik meie kahvlid on 100% originaalid ametlikelt edasimüüjatelt.'
  },
  '/toukeratas/klambrid': {
    title: 'Tõukeratta klamber | Single ja Double Bolt klambrid | Tõuks.ee',
    h1Title: 'Tõukeratta klamber',
    description: 'Tõukeratta klambrid — single-bolt, double-bolt ja 4-bolt. Standard ja oversized suurused. Blunt, Aztek, Drone. Tasuta saatmine Eestis.',
    shortDescription: 'Klamber kinnitab lenksu kahvli peale ja tagab turvalise ning kindla juhtimise. Meie valikus on tõukeratta klambrid single-bolt, double-bolt ja 4-bolt versioonides, nii standard- (31.8mm) kui oversized- (34.9mm) suuruses. Juhtivad brändid: Blunt, Aztek, Drone. Tasuta saatmine kogu Eestis.',
    longDescription: 'Tõukeratta klamber on väike, kuid kriitiliselt oluline komponent — see tagab, et lenks ei pööraks ega libiseks kahvlil sõidu ajal. Klambrid jagunevad poltide arvu järgi: single-bolt klamber on kergeim ja sobib park-sõitjatele; double-bolt annab tugevama kinnituse, eelistatud street-sõitjatele; 4-bolt klamber on maksimaalse kinnitusega pro-taseme kasutuseks. Klamber peab ühilduma lenksu välisläbimõõduga — standard lenks (31.8mm) vajab standard klambrit, oversized lenks (34.9mm) vajab oversized klambrit. SCS kompressioonisüsteemi puhul täidab klamber ka kompressiooni rolli. Vaata alati toote spetsifikatsiooni, et leida oma tõuksile sobiv klamber.'
  },
  '/toukeratas/lenksuotsad': {
    title: 'Tõukeratta lenksuotsad | Bar ends tõukeratastele | Tõuks.ee',
    h1Title: 'Tõukeratta lenksuotsad',
    description: 'Tõukeratta lenksuotsad (bar ends) alumiiniumist ja terasest. Kaitsevad lenksu otsi, parandavad haaret. Blunt, Aztek. Tasuta saatmine Eestis.',
    shortDescription: 'Lenksuotsad ehk bar ends on lenksu otste sisse paigaldatavad käepidemed, mis kaitsevad lenksu otsi kulumise ja kukkumiste eest ning parandavad haaret teatud trikkide ajal. Meie valikus on tõukeratta lenksuotsad alumiiniumist ja terasest, erinevatest värvidest. Tasuta kohaletoimetamine DPD, Omniva ja SmartPost pakiautomaatidesse.',
    longDescription: 'Tõukeratta lenksuotsad on lihtne, kuid kasulik komponent — need pikendavad lenksu haardepinda, kaitsevad lenksu otsi grindimise ja kukkumiste käigus ning lisavad personaalse puudutuse tõuksi viimistlusele. Lenksuotsad sobivad enamikule standardse siseläbimõõduga lenksudele (22mm) ja paigaldamine on lihtne — lüka sisse ja kinnita vajadusel poldiga. Alumiiniumist lenksuotsad on kergemad ja sobivad bar grindideks; teraskujud on vastupidavamad intensiivsele kasutusele. Paljud sõitjad valivad lenksuotsad nii funktsionaalsetel (kaitse) kui esteetilistel põhjustel. Kõik meie lenksuotsad on 100% originaalid.'
  },
  '/toukeratas/peegid': {
    title: 'Tõukeratta peegid | Grinding Pegs tõukeratastele | Tõuks.ee',
    h1Title: 'Tõukeratta peegid',
    description: 'Tõukeratta peegid (pegs) grinding-trikkideks — alumiinium ja teras, eri pikkused. Blunt. Tasuta saatmine Eestis.',
    shortDescription: 'Peegid on rattatelgedele paigaldatavad metallsilindrid, mida kasutatakse grinding-trikkide sooritamiseks — laskumiseks mööda relssi, äärte ja ledge\'e. Meie valikus on tõukeratta peegid alumiiniumist ja terasest, erinevatest pikkustest. Tasuta kohaletoimetamine DPD, Omniva ja SmartPost pakiautomaatidesse.',
    longDescription: 'Tõukeratta peegid on street-sõitja jaoks asendamatu vahend. Peegid paigaldatakse rattatelgede otsa ja need võimaldavad grindimistrikke — laskumist mööda relssi, äärte, ledge\'e ja konkreete. Peegid erinevad pikkuse ja materjali poolest: alumiiniumpeegid on kergemad ja libisevad paremini pinnalt pinnale; teraspeegid on vastupidavamad karmimale grindimisele. Pikkus mõjutab tasakaalu — pikemad peegid (4"+) annavad rohkem stabiilsust relsil, lühemad (3–3.5") on kergemad ja kompaktsemad. Enamik sõitjaid alustab kahe taga-peegiga — ees-peegid lisavad hiljem lisavõimalusi. Veendu enne ostu, et pegi sisediameeter ühildub su rattatelgega (tavaliselt 8mm).'
  },
  '/toukeratas/kaepidemed': {
    title: 'Tõukeratta käepidemed | Grips tõukeratastele | Tõuks.ee',
    h1Title: 'Tõukeratta käepidemed',
    description: 'Tõukeratta käepidemed (grips) — foam ja gumi, eri paksustes ja värvides. Blunt, Root Industries, Fuzion. Tasuta saatmine Eestis.',
    shortDescription: 'Käepidemed on ainus kontaktpunkt käe ja tõukeratta vahel — neil on suur mõju haarele, mugavusele ja trikkide täpsusele. Meie valikus on tõukeratta käepidemed vahtpolüuretaanist ja gumist, erinevatest paksustest ja värvidest, kõigilt juhtivalt brändilt. Tasuta kohaletoimetamine DPD, Omniva ja SmartPost pakiautomaatidesse.',
    longDescription: 'Tõukeratta käepidemed (grips) tulevad kahes põhimaterjalid: vahtpolüuretan (foam) — pehme, kerge, imab vibratsiooni, eelistatud park-sõitjatele; gumi — vastupidavam, kõvem haare, sobib street-sõitjatele. Käepidemete laius ja paksus mõjutab kontrolli: paksemad grips-id on mugavamad pikaks sõiduks, õhemad annavad tõukeratta täpsema tunde käes. Pikkus peaks sobima lenksu ulatusega — enamik on standardpikkuses (150–170mm). Käepidemed tuleks vahetada, kui need on liiga libedad, rebenenud või kulunud — intensiivse kasutuse korral vahetab enamik sõitjaid gripsid 3–6 kuu järel. Paigaldamine on lihtne: piserda lenksu otsa piritust, et grip libiseks sisse, seejärel lase kuivada.'
  },
  '/toukeratas/laste-toukeratas': {
    title: 'Laste Tõukeratas | Mini Trikitõuks Lastele | Tõuks.ee',
    h1Title: 'Laste tõukeratas — mini trikitõuks noortele sõitjatele',
    description: 'Laste tõukeratas 6–12-aastastele — päris trikitõuks väiksemas suuruses. Striker Lux Youth. Alumiinium, 110mm rattad, 2.95kg. Tasuta saatmine Eestis.',
    shortDescription: 'Tõuks.ee laste tõukerattad on päris trikisõiduks ehitatud mini tõukerattad — mitte odavad plastmassist mänguasjad, vaid sama tehnoloogia ja materjal mis proffidel, lihtsalt lapsele sobivasse suurusesse skaleeritud. Meie laste trikitõuksid sobivad 6–12-aastastele noortele sõitjatele. Tasuta kohaletoimetamine DPD, Omniva ja SmartPost pakiautomaatidesse.',
    longDescription: 'Laste tõukeratas on üks tähtsamaid otsuseid, mille lapsevanem saab oma lapse spordielu alguses teha. Odav plastikust tõuks jõuab prigi alla vähem kui aastaga ja ei õpeta lapsele ühtegi õiget harjumust — kehv haare, vale kaalu jaotus ja lagunev raam teevad väga kiiresti igavaks. Mini tõukeratas ehk trikitõukeratas lastele on hoopis teine maailm. Striker Lux Youth näitel: alumiinium 6061 T6 raam, 110mm ABEC-9 laagritega rattad, IHC kompressioon ja kaal vaid 2.95 kg — tõeline spordivarustus, mis talub aastatega kasvavat sõidukogemust. Laps, kes alustab 6–10-aastaselt professionaalsel laste tõukerattal, areneb märkimisväärselt kiiremini kui need, kes alustasid odavate toodetega. Noor aju imab liigutusi, tasakaalu ja ruumitaju endasse plahvatuslikult — mida varem päris varustusega alustada, seda sügavam on areng. Tõukeratas lastele on investeering spordisse, enesekindlusse ja loovusesse.'
  },
  '/toukeratas/gripteibid': {
    title: 'Tõukeratta gripteip | Griptape tõukeratta tallale | Tõuks.ee',
    h1Title: 'Tõukeratta gripteip',
    description: 'Tõukeratta gripteip talla jaoks — tugeva haardega isekleepuv kile. Blunt, Root Industries. Standardlaius, lõigatakse vastavalt tallale. Tasuta saatmine Eestis.',
    shortDescription: 'Gripteip on isekleepuv liivapaberikiht, mis paigaldatakse tõukeratta tallale — see tagab jala kindla haarde trikke sooritades ka niiskes ilmas. Meie valikus on tõukeratta gripteibid standardlaiuses, lõigatakse vastavalt talla suurusele. Tasuta kohaletoimetamine DPD, Omniva ja SmartPost pakiautomaatidesse.',
    longDescription: 'Tõukeratta gripteip on möödapääsmatu komponent iga tõukse peal. See on isekleepuv peenliivapaberikiht, mis kinnitatakse talla ülemisele pinnale ja tagab jala tugeva haarde ka niiskes ilmas ja intensiivsete trikkide ajal. Gripteip valitakse peamiselt paksuse ja struktuuritiheduse järgi — paksem annab rohkem haaret, õhem kulub aeglasemalt. Gripteip tuleks vahetada, kui see hakkab tõstma, on märgatavalt kulunud või jalg hakkab talla peal libisema. Paigaldamine on lihtne: puhasta tald, koori kile tagant, suru talla pinnale alates keskelt väljapoole, lõika üleliigne osa äärtest ära. Meie gripteip sobib enamikule tõukeratta talladele.'
  }
};

type FaqItem = { q: string; a: string };

function getBrandFaqs(brand: string): FaqItem[] {
  const name = brand.charAt(0).toUpperCase() + brand.slice(1);
  return [
    {
      q: `Mis bränd on ${name} ja kust nad pärinevad?`,
      a: `${name} on üks maailma tunnustatumatest trikitõukside brändidest, kelle tooteid kasutavad nii algajad kui ka maailmatasemel profisõitjad. Bränd on tuntud kõrge kvaliteedi, vastupidavuse ja innovaatiliste disainilahenduste poolest. Kõik Tõuks.ee-s müüdavad ${name} tooted on 100% originaalid, mis pärinevad ametlikelt edasimüüjatelt.`
    },
    {
      q: `Milliseid ${name} tooteid Tõuks.ee müüb?`,
      a: `Meie valikus on ${name} brändilt nii komplektseid tõukseid (complete scooters) kui ka eraldi varuosi — taldasid, lenksusid, rattaid ja muid komponente. Valik uueneb regulaarselt uute mudelitega. Vaata kõiki saadaolevaid ${name} tooteid sellelt lehelt.`
    },
    {
      q: `Kas ${name} tõuks sobib algajale?`,
      a: `Jah! ${name} toodab tõukseid erinevatele tasemetele. Algajatele sobivad ${name} entry-level ja kesktaseme complete scooters mudelid, mis on töökindlad, ohutud ja hea hinna-kvaliteedi suhtega. Kogenumad sõitjad valivad ${name} pro-taseme mudeleid ja custom varuosi.`
    },
    {
      q: `Kas ${name} osad ühilduvad teiste brändide komponentidega?`,
      a: `Paljud ${name} varuosad järgivad tööstuse standardmõõte ja ühilduvad teiste brändide komponentidega — eriti tallad, rattad ja lenksud. Siiski soovitame alati enne ostmist kontrollida ühilduvust (kompressioonisüsteem, rattasuurus, peatoru tüüp). Kahtluse korral kirjuta meile — aitame hea meelega!`
    },
    {
      q: `Mis on ${name} garantiitingimused?`,
      a: `Kõik ${name} tooted Tõuks.ee-st on originaalid ja neil kehtib tootja garantii tootmisdefektide vastu. Garantii ei kata normaalset kulumist ega kasutaja põhjustatud kahjustusi. Täpsed garantiitingimused on märgitud iga toote juures. Probleemide korral võta meiega ühendust.`
    },
    {
      q: `Kuidas valida õiget ${name} mudelit?`,
      a: `${name} mudeli valikul arvesta: (1) sõitja taset ja vanust — algajale sobib entry-level complete, profisõitjale pro-tase; (2) sõitlusstiile — park vs street; (3) kasvu — lenks peaks ulatuma puusani. Pole kindel? Kirjuta meile ja aitame leida sobivaima ${name} mudeli!`
    },
  ];
}

const FAQ_CONTENT: Record<string, FaqItem[]> = {
  '/toukeratas': [
    {
      q: 'Mis vahe on trikitõukerattal ja tavalisel tõukerattal?',
      a: 'Trikitõukeratas (pro scooter) on spetsiaalselt trikkide ja akrobaatika jaoks disainitud — see ei voldi, on tugevamast materjalist ja omab jäigast ühes tükis raami. Tavaline foldable tõukeratas on mõeldud transpordi jaoks ning ei talu skatepargis tehtavaid trikke ja kukkumisi.'
    },
    {
      q: 'Kas trikitõukeratas sobib algajale ja lapsele?',
      a: 'Jah! Meie valikus on trikitõukseid alates 5–6 aastasest lapsest kuni täiskasvanud profisõitjateni. Algajatele soovitame complete tõuksi (valmis komplekteeritud), mis on kohe sõiduks valmis, turvalisem ja sobivat hinna-kvaliteedi suhet pakkuv valik. Vanemale algajale (12+) sobivad kesktaseme mudelid.'
    },
    {
      q: 'Kuidas valida õiget suurust — kui kõrge peaks lenks olema?',
      a: 'Reegel on lihtne: seistes tõukse peal peaks lenks ulatuma puusa ja nabani. See tagab maksimaalse mugavuse ja tasakaalu. Lapsele ära osta liiga suurt tõuksi mõeldes "ta kasvab sisse" — vale suurus raskendab õppimist ja suurendab vigastuste riski.'
    },
    {
      q: 'Mis on complete scooter ja kas ma peaksin seda valima?',
      a: 'Complete scooter on tehase poolt kokku pandud tõuks, kus kõik osad — tald, lenks, rattad, klamber — on omavahel ühilduvad ja kohe kasutamiseks valmis. Algajatele on see ideaalne valik, sest see on soodsam kui ise osadest ehitamine ja kõik osad töötavad koos hästi.'
    },
    {
      q: 'Mis on erinevus kesktaseme, pro-taseme, street- ja park-tõuksi vahel?',
      a: 'Kesktase on algajatele ja noortele sõitjatele — töökindel ja soodsa hinnaga. Pro tase on kogenud sõitjatele — kergem, tugevam, premium osad. Street-tõuks on disainitud maapealseteks trikkideks ja grindimiseks — laiem ja tugevam. Park-tõuks on skatepargis õhutrikke tegema — kergem ja kompaktsem.'
    },
    {
      q: 'Milliseid tõuksibrände te müüte?',
      a: 'Meie valikus on maailma juhtivad trikitõukide brändid: Blunt Scooters, Aztek, Drone, North Scooters, Lucky Scooters, Striker, Tilt, Root Industries ja teised. Kõik tooted on 100% originaalid, mis pärinevad ametlikelt edasimüüjatelt.'
    },
    {
      q: 'Kas ma saan tõuksi osi ka eraldi osta ja kohandada?',
      a: 'Jah! Tõuks.ee müüb nii komplektseid tõukseid kui ka eraldi varuosi — taldasid, lenksusid, rattaid, klambreid, kahvleid ja muud. Kogenumad sõitjad ehitavad tihti oma custom tõuksi, valides iga osa eraldi oma eelistuste järgi.'
    },
    {
      q: 'Mis on triki tõukeratas ja mille poolest see erineb tavalisest tõuksist?',
      a: 'Triki tõukeratas (ingl. trick scooter, pro scooter) on jäiga raamiga sportvarustus, mille eesmärk on akrobaatika — hüpped, grindid, roteeringud ja muud trikid. Erinevalt tavalisest foldable tõuksist ei voldi trikitõukeratas kokku, on tugevamast materjalist (6061 alumiinium) ning talub suuri koormusi ja kukkumisi. Trikit tõukerattad on disainitud eelkõige skateparki ja tänavasse, mitte transpordi jaoks.'
    },
    {
      q: 'Kust osta tõukerattad Eestis — mis on parim valik?',
      a: 'Tõuks.ee on Eesti ainuke spetsialiseerunud trikitõukeratta pood, kust leiad nii komplektseid tõukseid algajatele kui ka pro-taseme mudeleid kogenud sõitjatele. Meie tõukerattad on 100% originaalid, mis pärinevad maailma juhtivate brändide ametlikelt edasimüüjatelt. Tasuta saatmine Eestis DPD, Omniva ja SmartPost pakiautomaatidesse — tellimine on kiire ja lihtne.'
    },
    {
      q: 'Kust leida tõukeratta varuosad — tallad, lenksud, rattad ja klambrid?',
      a: 'Kõik tõukeratta varuosad leiad Tõuks.ee-st — tallad, lenksud, rattad, kahvlid, klambrid, käepidemed, gripteibid ja muud komponendid. Meie tõukeratta varuosad sobivad eri brändidega ja tasemetega. Kõik varuosad toimetatakse kätte kiirelt kogu Eestis. Pole kindel, milline varuosa sinu tõuksiga sobib? Kirjuta meile — aitame hea meelega!'
    },
  ],
  '/toukeratas/tald': [
    {
      q: 'Mis on tõukeratta tald (deck) ja miks see oluline on?',
      a: 'Tald ehk deck on tõukeratta alus — platvorm, millel sõitja seisab. See on tõuksi kõige olulisem osa, mis määrab sõidu stabiilsuse, kerguse ja vastupidavuse. Õige tald sobib sinu kehaehituse, jalasuuruse ja sõilustiili järgi.'
    },
    {
      q: 'Milline talla laius mulle sobib?',
      a: 'Talla laius sõltub sõitlustiili. Park-sõitjatele sobib kitsam tald (4.5"–5"), mis on kergem ja võimaldab kiiremaid rotatsioone. Street-sõitjatele sobib laiem tald (5.5"–7"), mis annab rohkem stabiilsust grindimiseks. Algajatele soovitame 4.5"–5" — see on universaalne ja kergem hallata.'
    },
    {
      q: 'Kas lühem või pikem tald on parem?',
      a: 'Lühem tald (49–53 cm) on kiirem pöördeid tegema ja sobib paremini park-sõitjatele ja väiksematele sõitjatele. Pikem tald (53–59 cm) annab rohkem stabiilsust ja on mugavam seista peal — sobib street-sõitjatele ja pikematele sõitjatele.'
    },
    {
      q: 'Millest tald tehtud on ja mis materjal on parim?',
      a: 'Enamik professionaalseid taldasid on valmistatud alumiinium 6061 või 6082-T6 sulamist — see on kerge ja väga vastupidav. Alumiinium on parim valik jõudluse ja kaalu vahel. Odavamad tallad on terasest, mis on raskemad. Teras on sobiv algajatele, alumiinium on parem kesktaseme ja pro-sõitjatele.'
    },
    {
      q: 'Kas uus tald ühildub minu olemasoleva tõuksiga?',
      a: 'Enne ühilduvuse kontrollimist vaata kolme asja: (1) rattasuurus — tald peab mahutama sinu rataste läbimõõtu; (2) peatoru tüüp — integreeritud peatoru vajab integreeritud headset-i; (3) kompressioonisüsteem (SCS/HIC/IHC). Kahtluse korral kirjuta meile ja aitame ühilduvust kontrollida!'
    },
    {
      q: 'Kui kaua tald kestab ja millal tuleb vahetada?',
      a: 'Kvaliteetne alumiiniumtald peaks kestma vähemalt 6–12 kuud normaalse kasutuse juures. Aeg vahetada on siis, kui tald on pragunenud, painutatud, peatoru on kahjustunud, või grindimispinnad on täiesti ära kulunud. Regulaarne hooldus ja hoidmine kuivas kohas pikendab talla eluiga oluliselt.'
    },
    {
      q: 'Mis on konkav (concave) ja miks see oluline on?',
      a: 'Konkav on talla platvormi kerge kumerus — nagu õhuke kauss. See aitab jalal talla peal püsida ilma alla vaatamata ja annab parema grip-tunde trikkide ajal. Tugevam konkav sobib kogenumale sõitjale, lamedama konkaviga tald on algajale mugavam.'
    },
  ],
  '/toukeratas/lenks': [
    {
      q: 'Mis on tõukeratta lenks (bars) ja kuidas see sõitu mõjutab?',
      a: 'Lenks on T- või Y-kujuline käepide, mida sõitja kasutab juhtimiseks ja trikkide sooritamiseks. Lenks mõjutab otseselt sõitja kontrolli, mugavust ja võimet trikke teha. Õige lenks tähendab paremat tasakaalu ja efektiivsemat sõitu.'
    },
    {
      q: 'Mis kõrgusega lenks mulle sobib?',
      a: 'Reegel: lenks peaks ulatuma puusast nabani, kui seisad tõukse peal. See annab parima tasakaalu mugavuse ja kontrolli vahel. Park-sõitjad eelistavad madalamaid lenksusid (parem kontroll õhus), street-sõitjad kõrgemaid (parem stabiilsus grindimiseks). Ära osta lapsele liiga kõrget lenksu — vale kõrgus raskendab õppimist.'
    },
    {
      q: 'Mis on erinevus SCS, HIC ja IHC kompressioonisüsteemide vahel?',
      a: 'SCS (Standard Compression System) on kõige tugevam ja raskem — sobib street-sõitjatele. HIC (Hidden Internal Compression) on kesktee — kergem kui SCS, vajab oversized teraslenksut koos slitiga. IHC (Integrated Headset Compression) on kergeim variant — sobib park-sõitjatele, töötab standardse alumiiniumlenksuga. Meie tooteinfo näitab alati lenksule sobivat kompressioonisüsteemi.'
    },
    {
      q: 'Kas lenksu saab lühemaks lõigata?',
      a: 'Jah, metallist lenksuid saab lõigata. Paljud sõitjad lõikavad lenksu soovitud kõrgusele. Alumiinium- ja teraslenksuid on lihtne lõigata metallsaega. Oluline: lõikamise järel eemalda sliti katteks teip ning puhasta ääred. Mõnele kompressioonisüsteemile (IHC/HIC) on oluline ka sliti õige pikkus.'
    },
    {
      q: 'Milline materjal on parem — teras, alumiinium või titaan?',
      a: 'Teras on kõige levinum ja soodsam — sobib algajatele. Alumiinium on kerge ja vastupidav — populaarne kesktaseme ja pro-sõitjate seas. Titaan on kõige kergem ja tugevaim, aga ka kõige kallim — pro-sõitjate valik. Alumiinium on standardse sisemõõduga, kuid oversized välismõõduga, mistõttu ei ühildu HIC-süsteemiga.'
    },
    {
      q: 'Mis laius peaks olema lenksul?',
      a: 'Lenksu laius peaks ligikaudu vastama sõitja õlgade laiusele. Algajatele ja lastele sobivad laiemad lenksud (55–60 cm), mis pakuvad rohkem stabiilsust. Kogenumad sõitjad eelistavad kitsamaid lenksusid (45–55 cm), mis võimaldavad kiiremaid trikke ja paremad kontrolli.'
    },
    {
      q: 'Kuidas kontrollida, kas uus lenks ühildub minu tõuksiga?',
      a: 'Kontrolli kolme asja: (1) kompressioonisüsteem — SCS vajab lenksu ilma slitita, HIC vajab oversized teraslenksut slitiga, IHC vajab standardset lenksut slitiga; (2) lenksu siseläbimõõt peab klambriga ühilduma; (3) lenksu kõrgus peab sobima sinu kasvule. Kahtluse korral kirjuta meile!'
    },
  ],
  '/toukeratas/kesktase-toukerattad': [
    {
      q: 'Mis on kesktaseme tõukeratas ja kellele see sobib?',
      a: 'Kesktaseme tõukeratas on samm algaja mudeli ja pro-taseme vahel. See sobib sõitjatele, kes on juba algustrikid selgeks saanud ja tahavad liikuda edasi keerulisemate trikkideni nagu tailwhip, barspin ja 180-pöörded. Tavaliselt sobib see 9–14-aastastele ning täiskasvanud algajatele.'
    },
    {
      q: 'Mille poolest erineb kesktaseme tõukeratas algaja mudelist?',
      a: 'Kesktaseme tõukerattal on oluliselt paremad komponendid: alumiiniumist rattasüdamikud (vs nailonist), integreeritud peatoru (headset), tugevam keevisraamistik ja tavaliselt kergem raam. Need omadused muudavad sõidu täpsemaks, pikendavad tõuksi eluiga ja võimaldavad keerulisemaid trikke ohutult sooritada.'
    },
    {
      q: 'Millal on õige aeg uuendada algaja tõukselt kesktasemele?',
      a: 'Kesktasemele liikumise märgid: oled selgeks saanud bunny hop\'i, grindimise, 180 pöörde ja proovid esimesi tailwhip\'e või barspin\'e. Samuti siis, kui su praeguse tõuksi osad hakkavad kiiresti kuluma — see näitab, et oled tõuksi võimetele kasvanud.'
    },
    {
      q: 'Kas kesktaseme tõuksi osi saab hiljem vahetada?',
      a: 'Jah! Üks kesktaseme tõuksi plussidest on see, et kõiki osi — talda, lenksu, rattaid, kahvlit, klambrit — saab hiljem eraldi uuendada. See võimaldab tõuksi kohandada oma areneva sõitlustiili järgi ilma täiesti uut tõuksi ostmata.'
    },
    {
      q: 'Milline kaal on kesktaseme tõuksil ja miks see oluline on?',
      a: 'Kesktaseme tõuksid kaaluvad tavaliselt 3.2–3.8 kg. Kaal mõjutab trikke oluliselt — liiga raske tõuks raskendab õhutrikke ja tailwhip\'e, liiga kerge võib tunduda ebastabiilne. Kesktase leiab hea tasakaalu tugevuse ja kaalu vahel.'
    },
    {
      q: 'Milliseid trikke saab kesktaseme tõuksiga teha?',
      a: 'Kesktaseme tõukeratastega saab sooritada kõiki tähtsamaid trikke: tailwhip, barspin, 180, 360, grindid (50-50, boardslide, feeble), manualid ja esimesed kombinatsioonid. Pro-tase avab lisaks keerulisemad double tailwhip\'id ja kõrgtasemel combo\'d.'
    },
    {
      q: 'Mis hinnaga on kesktaseme tõukerattad?',
      a: 'Kesktaseme complete tõukerattad jäävad tavaliselt 130–250 euro vahele. See on oluliselt rohkem kui algaja mudelid (80–130 €), kuid sellega kaasnevad ka märkimisväärselt paremad komponendid ja pikem kasutusiga.'
    },
  ],
  '/toukeratas/pro-tase-toukerattad': [
    {
      q: 'Mis on pro-taseme tõukeratas?',
      a: 'Pro-taseme tõukeratas on loodud kogenud sõitjatele, kes sooritavad regulaarselt keerulisi trikke skatepargis ja tänavatel. Need tõuksid kasutavad premium-klassi komponente — sageli sama kvaliteediga, mis leiab kohandatud (custom) ülesehitustes — ning on äärmiselt kerged ja vastupidavad.'
    },
    {
      q: 'Mille poolest erineb pro-tase kesktasemest?',
      a: 'Pro-taseme tõuksi eristab premium materjalid (titaan, kõrgklass alumiinium), oluliselt kergem kogukaal (alla 3 kg), kõrge täpsusega valmistatud osad, integreeritud headset ja tihti SCS kompressioonisüsteem. Hinnaerinevus on märgatav, kuid jõudluserinevus samuti.'
    },
    {
      q: 'Kas pro-taseme complete tõuks on parem kui custom ehitus?',
      a: 'Pro-taseme complete tõuksid pakuvad tehase poolt tasakaalustatud ja sobivaid komponente eelisega, et kõik osad on omavahel testitud. Custom ehitus annab täieliku kontrolli iga osa üle, kuid maksab rohkem ja nõuab rohkem teadmisi. Pro-complete on hea valik isegi kogenud sõitjatele, kes ei taha ise ehitada.'
    },
    {
      q: 'Kui kerge peaks pro-taseme tõuks olema?',
      a: 'Enamus pro sõitjaid eelistab tõuksi, mis kaalub alla 3 kg (u. 6.5 lbs). Kergem tõuks võimaldab kõrgemaid hüppeid, kiiremat tailwhip\'i rotatsiooni ja kergemini sooritatavaid õhutrikke. Titaanist lenksud on üks levinumaid viise kaalu drastiliseks vähendamiseks.'
    },
    {
      q: 'Kellele on pro-taseme tõukeratas mõeldud?',
      a: 'Pro-tase sobib kogenud sõitjatele, kes on juba selgeks saanud kompleksed trikid — double tailwhip\'id, combo\'d, kõrgtasemel grindid — ja vajavad tööriistu oma piire edasi nihutamiseks. Samuti sobib neile, kes võistlevad või filme/videoid teevad.'
    },
    {
      q: 'Kas pro-taseme tõuksi osi saab edasi uuendada?',
      a: 'Pro-taseme tõuksidel on juba premium osad, mistõttu uuendamise vajadus on väiksem. Kuid kogenud sõitjad vahetavad sageli lenksu, talda ja rataid oma täpse stiili järgi. Kõik standardsuuruste osad ühilduvad tavaliselt teiste brändide pro-tasemel komponentidega.'
    },
  ],
  '/toukeratas/street-toukerattad': [
    {
      q: 'Mis on street-sõitlusstiil ja mille poolest see erineb park-sõitlusest?',
      a: 'Street-sõitlus tähendab trikkide sooritamist linnakeskkonnas ja tänaval — relsidel, treppidel, äärekividel, ledge\'idel ja muudel loodusliku keskkonna takistustel. Park-sõitlus toimub skatepargis rampidel ja kausidel. Street nõuab tugevamat ja raskemat seadistust, park kergemat ja kompaktsemat.'
    },
    {
      q: 'Milline tald sobib street-sõitluseks?',
      a: 'Street-sõitluseks sobib laiem tald (5.5"–7") box-cut kujuga — sirged, kandilised küljed annavad parema grindimispinna relsidel ja ledge\'idel. Laiem tald pakub ka rohkem stabiilsust maandumisteks treppidelt. Street-taldadel on tavaliselt tugevam keevisõmblus ja paksem alumiinium.'
    },
    {
      q: 'Millist kompressioonisüsteemi kasutatakse street-tõuksides?',
      a: 'Street-sõitjad eelistavad SCS (Standard Compression System) kompressiooni, kuna see on kõige tugevam ja ei lase lenksul keerata. SCS takistab lenksu libisemist tugeva maandumise ja grindimise ajal — väga oluline omadus tänavate reaalsetes tingimustes.'
    },
    {
      q: 'Kas street-tõuksil on vaja pegasid (peg)?',
      a: 'Jah, pegad on street-sõitluseks oluline lisa. Pegad on silindrilised metalltorud, mis kinnituvad rataste teljele, ja võimaldavad peg-grinde relsidel ning äärekividel. Enamikul street-tõukse saab pegasid lisada ja eemaldada. Terasest pegad on algajatele, alumiiniumist kergemad kuid kiiremini kuluvad.'
    },
    {
      q: 'Kas street-tõuksiga saab ka skatepargis sõita?',
      a: 'Jah, kuid see pole ideaalne. Street-seadistus on raskem ja jäigem, mistõttu õhutrikid ja tailwhip\'id nõuavad rohkem jõudu. Kui sõidad nii tänaval kui pargis, soovitame hübriidseadistust — keskmise laiusega tald ja universaalsed komponendid — või kasuta kahte eri tõuksi eri tingimustes.'
    },
    {
      q: 'Millised on street-sõitjate peamised trikid?',
      a: 'Street-sõitjate klassikalised trikid: 50-50 grind, boardslide, feeble grind, nosegrind, lipslide ja smith grind relsidel ja ledge\'idel; gap jump\'id ja stair set\'id; manual\'id äärekividel; ning näidiskorras ka barspin\'id ja tailwhip\'id maapinnal. Street on tehniline ja loov sõitlusstiil.'
    },
  ],
  '/toukeratas/pargi-toukerattad': [
    {
      q: 'Mis on park-sõitlusstiil ja millist tõuksi see nõuab?',
      a: 'Park-sõitlus toimub skatepargis — rampidel, quarter-pipe\'idel, kaussidel (bowl) ja muudel pargikonstruktsioonidel. Fookus on voolamisel, kõrgetel hüpetel ja õhutrikkidel. Park-tõuks peab olema kerge ja kompaktne, et võimaldada tailwhip\'e, barspin\'e ja flips\'e kõrgelt rambi üleval.'
    },
    {
      q: 'Miks on park-tõuks kergem kui street-tõuks?',
      a: 'Park-sõitjad vajavad kerget seadistust, sest raskemaid tõukseid on raskem õhus manipuleerida. Liigne raskus raskendab tailwhip\'i rotatsiooni, barspin\'i kiirust ja flips\'e. Park-tõuksidel on tavaliselt kitsam tald, alumiinium- või titaanlenks ning IHC/HIC kompressioon — kõik kerguse huvides.'
    },
    {
      q: 'Milline tald sobib park-sõitluseks?',
      a: 'Park-sõitluseks sobib kitsam tald (4.5"–5.5") peg-cut kujuga — mõnevõrra ümardatud küljed vähendavad kaalu ja suurendavad manööverduvust. Kitsam tald keerab kiiremini, mis on oluline tailwhip\'ide ja flips\'ide jaoks. Samas ei sobi see hästi grindimiseks, mis ongi park-sõitluses vähem levinud.'
    },
    {
      q: 'Milliseid trikke tehakse park-sõitluses?',
      a: 'Park-sõitluse klassikalised trikid: tailwhip, barspin, 360, 540 (rampilt); flair (tagasomersault + barspin); no-hander, no-footer; Superman; ramp grindid (äärel). Eesmärk on maksimeerida kõrgust, õhus veedetud aega ja trikkide stiili. Park-sõitlus on visuaalselt väga atraktiivne vaatajatele.'
    },
    {
      q: 'Kas park-tõuksiga saab ka tänavatel sõita?',
      a: 'Park-tõuks ei sobi hästi tänavasõitluseks, kuna on liiga kerge ja kitsas stabiilseks grindimiseks relsidel ja ledge\'idel. Park-tõuks on eelkõige rampide ja kausside jaoks. Tänavale minekuks vajad laiemat ja tugevamat street-seadistust.'
    },
    {
      q: 'Mis kompressioonisüsteem sobib park-sõitluse tõuksile?',
      a: 'Park-sõitjad eelistavad IHC (kergeim) või HIC kompressiooni, kuna mõlemad on kergemad kui SCS. Kergem kompressioon tähendab, et tõuks on kergem üldiselt — oluline parema jõudluse saavutamiseks kõrgetel rampidel ja keeruliste trikkide ajal. SCS on park-tõuksis harva kasutatav.'
    },
    {
      q: 'Milliseid kaitsevahendeid vajab park-sõitluses?',
      a: 'Park-sõitluses on kaitsevahendid väga olulised, kuna hüpped on kõrged ja maandumised kõvad. Kohustuslik kiiver; soovitatav põlvekaitsed, küünarkaitsed ja randmekaitsed. Pargis õppijatele soovitame alustada madalate rampidega ja lisada keerukust järk-järgult.'
    },
  ],
  '/toukeratas/brandid': [
    {
      q: 'Milliseid trikitõukside brände Tõuks.ee müüb?',
      a: 'Tõuks.ee valikus on maailma juhtivad trikitõukside brändid: Blunt Scooters, Aztek, Drone, North Scooters, Lucky Scooters, Striker, Tilt, Root Industries ja teised. Kõik tooted on 100% originaalid ametlikelt edasimüüjatelt. Meie valimine on hoolikas — müüme ainult tõestatud kvaliteediga brände.'
    },
    {
      q: 'Milline bränd sobib algajale?',
      a: 'Algajatele soovitame Drone, Striker või Blunt entry-level mudeleid. Need brändid pakuvad töökindlaid ja turvaliselt kokkupandud tõukseid mõistliku hinnaga. Algajale on kõige tähtsam, et tõuks oleks õige suuruse ja vastupidavusega — mitte bränd ise.'
    },
    {
      q: 'Milline bränd on parim kogenud sõitjale?',
      a: 'Kogenumad sõitjad eelistavad Aztek, North, Blunt pro-tase ja Lucky pro mudeleid. Need brändid pakuvad premium komponente, äärmiselt kerget konstruktsiooni ja professionaalsete sõitjate tagasisidet. Õige valik sõltub sinu sõitlusstiili (street vs park) ja eelarve suurusest.'
    },
    {
      q: 'Kas eri brändide osad ühilduvad omavahel?',
      a: 'Paljud standardsuuruses osad (rattad, klambrid, pegad) ühilduvad brändide vahel, kuid mitte kõik. Kompressioonisüsteem (SCS/HIC/IHC) on kõige olulisem ühilduvustegur. Soovitame sama brändi komponente kasutada, eriti tald+kahvel+lenks kombinatsioonis. Kahtluse korral küsi meie käest!'
    },
    {
      q: 'Mis vahe on Blunt Scootersi ja Azteki vahel?',
      a: 'Blunt Scooters on Uus-Meremaa päritolu bränd, kes on üks trikitõukside tööstuse pioneere — tuntud versatiimsete ja hea hinnaga tõukste poolest. Aztek on spetsiifiliselt premium varuosade poolest tuntud, eriti dekid ja lenksud. Mõlemad on maailmatasemel brändid, valik sõltub eelistusest.'
    },
    {
      q: 'Kas kõikidel brändidel on garantii?',
      a: 'Jah, kõik Tõuks.ee-s müüdavad tooted on originaalid ja neile kehtib tootja garantii tootmisdefektide vastu. Garantiiaeg varieerub bränditi. Garantii ei kata normaalset kulumist, kukkumiskahjustusi ega kasutaja põhjustatud vigastusi. Täpsemad tingimused leiad iga toote juures.'
    },
  ],
  '/toukeratas/rattad': [
    {
      q: 'Milline rattasuurus sobib minu tõuksile — 100mm, 110mm või 120mm?',
      a: '100mm rattad on kergemad ja kiiremad pööretes — sobivad park-sõitjatele, kes vajavad kiiret rotatsiooni trikkide ajal. 110mm on universaalne valik hea kiirenduse ja stabiilsusega. 120mm rattad annavad suurema kiiruse ja sujuvama sõidu tänaval. Kontrolli talla spetsifikatsiooni, et teada maksimaalne rattasuurus.'
    },
    {
      q: 'Mis vahe on alumiinium- ja nailonist rattasüdamikul?',
      a: 'Alumiiniumsüdamik on tugevam, kergem ja kestab oluliselt kauem — see on valik kogenud sõitjatele. Nailonsüdamik on odavam, kuid murdub kergemini tugeva kukkumise või grindimise käigus. Pro-taseme sõitjad kasutavad alati alumiiniumsüdamikuga rattaid.'
    },
    {
      q: 'Kas tõukeratta rattad tulevad laagritega?',
      a: 'Enamik tõukeratta rattaid tarnitakse laagritega (ABEC-7 või ABEC-9). Kontrolli toote kirjeldusest, kas laagrid on komplektis. Kui ei, sobivad standardsed 608 ZZ laagrid (8mm sisediameeter). ABEC-9 laagrid on täpsemad ja sobivad kiiremaks sõiduks.'
    },
    {
      q: 'Millal tuleks tõukeratta rattad välja vahetada?',
      a: 'Vaheta rattad siis, kui uretaankiht on kulunud südamikuni, ratas on pragunenud, või sõit muutub ebaühtlaseks ja mürarikkaks. Profisõitjad vahetavad rattaid iga 3–6 kuu järel intensiivse kasutuse korral. Rattavahetust saab viia läbi ilma spetsiaalsete tööriistadeta.'
    },
    {
      q: 'Kas rattad tulevad individuaalselt või paarides?',
      a: 'Enamik tõukeratta rattaid müüakse individuaalselt (ühe kaupa). Mõned paketid müüakse paarides — kontrolli toote kirjeldusest, mitu ratast komplekti kuulub. Tõukerattal on kaks ratast — esi- ja tagaratas. Mõlemad rattad ei pea olema ühesugused.'
    },
  ],
  '/toukeratas/kahvlid': [
    {
      q: 'Mis kompressioonisüsteemiga tõukeratta kahvel ühildub?',
      a: 'Kahvli kompressiooniühilduvus sõltub peatoru tüübist: IHC kahvlid töötavad koos IHC lenksuga (standardne läbimõõt, slitiga); HIC kahvlid nõuavad oversized teraslenksut slitiga; SCS kahvlid töötavad peaaegu iga lenksuga — kõige universaalsem, kuid raskem. Vaata tootelehtül täpseid andmeid.'
    },
    {
      q: 'Kas kahvel on ühilduv minu talla rattaauguga?',
      a: 'Kontrolli kahvli rattaauku (dropout) ja ratta teljeläbimõõtu (tavaliselt 8mm). Enamik standardseid kahvleid on 8mm telgega ühilduvad. Veendu ka, et kahvel on õige rattasuuruse jaoks — 100mm, 110mm või 120mm rattad vajavad vastavaid kahvleid.'
    },
    {
      q: 'Kas pean ostma ka uue headset-i (peatoru laagrikomplekt)?',
      a: 'See sõltub olukorrast. Kui vahetad sama tüüpi kahvlit, võib vana headset sobida edasi. Kui vahetad kahvli tüüpi (nt IHC → HIC), on uus headset vajalik. Integreeritud headset on tavaline valik, ülemised ja alumised laagrid peavad sobima kahvli peatoru mõõtudega.'
    },
    {
      q: 'Milline materjal on parem — alumiinium või teras?',
      a: 'Alumiiniumkahvel on kergem — sobib park-sõitjatele, kes vajavad kerget seadistust õhutrikkideks. Teraskahvel on tugevam ja raskem — sobib street-sõitjatele intensiivseks tänavasõiduks. Enamik pro-sõitjaid kasutab alumiiniumkahvlit park-sõiduks ja terast tänavasõiduks.'
    },
  ],
  '/toukeratas/klambrid': [
    {
      q: 'Mis vahe on single-bolt, double-bolt ja 4-bolt klambril?',
      a: 'Single-bolt klamber (1 polt) on kergeim — sobib park-sõitjatele ja kergemale kasutusele. Double-bolt klamber (2 polti) annab tugevama kinnituse — sobib street-sõitjatele, kelle lenks kipub libisema. 4-bolt klamber on maksimaalse kinnitusega — pro-taseme kasutuseks intensiivsel tänavasõidul.'
    },
    {
      q: 'Kuidas teada, milline klamber ühildub minu lenksuga?',
      a: 'Klamber peab ühilduma lenksu välisläbimõõduga: standardne lenks → standard klamber (31.8mm sisediameeter); oversized lenks → oversized klamber (34.9mm sisediameeter). Kontrolli lenksu spetsifikatsioonist selle välisläbimõõtu enne ostmist.'
    },
    {
      q: 'Kas klamber töötab koos minu kompressioonisüsteemiga?',
      a: 'SCS kompressioonisüsteemiga kasutad klambrit kompressiooni osana — klamber surub lenksu peatoru peale. IHC ja HIC puhul täidab klamber ainult kinnitamisrolli. Veendu, et su klamber on vastavalt märgistatud (SCS klamber vs standard klamber).'
    },
    {
      q: 'Kas klamber on lihtne ise paigaldada?',
      a: 'Jah, klambri paigaldamine on üsna lihtne. Vajad ainult alleni võtit (tavaliselt 4mm või 5mm). Kinnita klamber ühtlaselt, järgides tootja momentvõtit (tavaliselt 5–8 Nm). Ärge kinnitage liiga tugevalt — see võib kahjustada lenksu või kahvlit.'
    },
  ],
  '/toukeratas/lenksuotsad': [
    {
      q: 'Mis on tõukeratta lenksuotsad ja milleks neid kasutatakse?',
      a: 'Lenksuotsad (bar ends) on lenksu otste sisse paigaldatavad käepidemed. Need laiendavad haardepinda, kaitsevad lenksu otsi kukkumiste ja grindimise eest ning parandavad kontrolli teatud trikkide ajal. Paljud sõitjad kasutavad neid ka esteetilisel eesmärgil.'
    },
    {
      q: 'Kas lenksuotsad sobivad kõigile lenksudele?',
      a: 'Enamik lenksuotsi on mõeldud standardse siseläbimõõduga (22mm) lenksudele. Enne ostu kontrolli oma lenksu siseläbimõõtu. Paigaldamine on lihtne — lüka sisse ja kinnita vajadusel poldiga. Alumiiniumist lenksuotsad on kergemad, terasest vastupidavamad.'
    },
    {
      q: 'Kas lenksuotsad sobivad grindimiseks?',
      a: 'Jah — alumiiniumist ja teraskujulised lenksuotsad sobivad bar grinding trikkideks (grinding lenksuga relsil). Need libisevad pinnalt paremini kui bare lenksu alumiinium. Aga veendu, et lenksuotsad on kindlalt kinnitatud enne grindimistrikke.'
    },
  ],
  '/toukeratas/peegid': [
    {
      q: 'Mis on tõukeratta peegid ja milleks neid kasutatakse?',
      a: 'Tõukeratta peegid (pegs) on metallsilindrid, mis paigaldatakse rattatelgede otstesse. Neid kasutatakse grinding-trikkide sooritamiseks — laskumiseks mööda relssi, äärte, ledge\'e ja konkreete. Peegid on street-sõitjate jaoks asendamatu vahend.'
    },
    {
      q: 'Milline pikkus on parem — pikemad või lühemad peegid?',
      a: 'Pikemad peegid (4"+) annavad rohkem stabiilsust relsil — sobivad algajatele grinding-trikkideks. Lühemad peegid (3–3.5") on kergemad ja kompaktsemad — eelistavad kogenud sõitjad. Pikkuse valik sõltub ka grindimisstiili — relsi peal vs ledge\'il vs konkreedil.'
    },
    {
      q: 'Kas peegid paigaldatakse ees- või tagarattale?',
      a: 'Peegid saab paigaldada mõlemale rattale. Enamik sõitjaid alustab kahe taga-peegiga. Ees-peegid lisavad lisavõimalusi keerulisemateks grinding-trikkideks. Tavaliselt kasutatakse 2–4 peegi korraga, sõltuvalt sõitlusstiilist ja trikkidest.'
    },
    {
      q: 'Milline materjal on parem — alumiinium või teras?',
      a: 'Alumiiniumpeegid on kergemad ja libisevad paremini mööda pinda — sobivad grinding-trikkideks. Teraspeegid on vastupidavamad intensiivsele kasutusele, kuid raskemad. Pro-sõitjad kasutavad sageli alumiiniumpeegisid kiirema libisemise ja väiksema kaalu tõttu.'
    },
  ],
  '/toukeratas/kaepidemed': [
    {
      q: 'Kuidas valida sobivad tõukeratta käepidemed?',
      a: 'Käepidemete valikul arvesta materjali (foam vs gumi), paksust ja pikkust. Foam käepidemed on pehmed, kerged ja imavad vibratsiooni — sobivad park-sõitjatele. Gumi käepidemed on vastupidavamad intensiivsele kasutusele — eelistatud street-sõitjatele. Paksemad gripsid on mugavamad, õhemad annavad täpsema tunde.'
    },
    {
      q: 'Kuidas käepidemed kinnitada ja eemaldada?',
      a: 'Paigaldamiseks piserdage lenksu otsa piritust — grip libiseb sisse ilma liimita. Pärast piirituse aurustumist on grip kindlalt paigas. Eemaldamiseks kasutage teravat lõikurit lenksu ja gripi vahele ning pöörake maha. Ärge kasutage liimi — see muudab eemaldamise väga keeruliseks.'
    },
    {
      q: 'Kui tihti tuleks käepidemed vahetada?',
      a: 'Vaheta käepidemed, kui need on liiga libedad, rebenenud või kulunud. Intensiivse kasutuse korral vahetab enamik sõitjaid gripsid 3–6 kuu järel. Libedaks muutunud käepide on ohutu — jalg võib trikki tehes lenksult libiseda, mis põhjustab kukkumisi.'
    },
  ],
  '/toukeratas/laste-toukeratas': [
    {
      q: 'Mis vanusest saab laps alustada trikitõuksiga?',
      a: 'Enamik laste tõukerattaid on mõeldud alates 6-aastastele. Striker Lux Youth sobib 6–12-aastastele. Oluline on, et lenks ulatuks lapse puusani — see tagab maksimaalse kontrolli ja tasakaalu. Mida varem alustada päris trikitõuksiga (mitte odava plastikuga), seda kiiremini laps areneb ja trikid selgeks saab.'
    },
    {
      q: 'Mis vahe on laste tõukerattal ja tavalise lapse plastiktõuksel?',
      a: 'Plastiktõuks on mõeldud transpordi jaoks — see kaalub vähem, volditakse kokku ja on odavast plastikust tehtud. Laste trikitõukeratas on päris spordivarustus: jäik alumiiniumraam, professionaalsed ABEC-9 laagritega rattad, IHC kompressioon ja võime taluda skatepargi trikke ja koormust aastaid. Plastiktõuks lõhutakse kuu-kahe jooksul, laste trikitõuks kestab lapse kasvulooks.'
    },
    {
      q: 'Kas laste tõukeratas on ohutu?',
      a: 'Jah — Striker Lux Youth on mõeldud maksimaalse ohutuse ja vastupidavusega. Täismetallist raam ei murdu ootamatult nagu plastikust tõuksid, alumiiniumkomponendid taluvad koormust ja rattad püsivad stabiilsed. Soovitatav on alati kanda kiivreid ja põlvekaitseid — nii plastikul kui trikitõuksel.'
    },
    {
      q: 'Mis suurusega laste tõukeratas minu lapsele sobib?',
      a: 'Striker Lux Youth on mõeldud kasvule 95–120 cm ja soovituslik vanus on 6–12 aastat. Peamine reegel: lenks peaks ulatuma lapse puusani seistes tõukse peal. Lapse täna 105 cm kasvu juures sobib see suurepäraselt. Ärge ostke lapsele liiga suurt tõuksi arvestades "ta kasvab sisse" — vale suurus takistab õppimist.'
    },
    {
      q: 'Kas laps peab olema kogenud sõitja, et osta päris trikitõuks?',
      a: 'Ei! Striker Lux Youth sobib nii täiesti algajatele (tasemeks Kesktase) kui ka edasijõudnutele. Tegelikult on soovitatav alustada kohe päris trikitõuksiga — laps õpib kiiremini õiget tehnikat, areneb kiiremini ja motivatsioon püsib kõrgemal. Odav plastiktõuks annab vale tehnika harjumused, millest on hiljem raske lahti saada.'
    },
    {
      q: 'Kas laste tõukeratast on lihtne kokku panna?',
      a: 'Striker Lux Youth tarnitakse osaliselt kokkupandult (partially assembled). Tavaliselt tuleb kinnitada lenks klambriga — protsess võtab 5–10 minutit ja ei nõua erilisi tööriistu. Kaasas on paigaldusjuhend. Pärast kokkupanekut on tõuks kohe sõiduks valmis.'
    },
  ],
  '/toukeratas/gripteibid': [
    {
      q: 'Kuidas gripteip tõukeratta tallale paigaldada?',
      a: 'Puhasta tald vana gripteibist. Koori uuelt gripteibilt kaitsekilpt tagant. Suru talla pinnale alates keskelt väljapoole, et mulle ei tekiks. Kasuta lenksu saba või mündi äärt, et gripteip tihedalt kinnitada. Viiluta äärtest üle ja lõika üleliigne osa ära terava lõikuriga.'
    },
    {
      q: 'Kui tihti tuleks gripteip vahetada?',
      a: 'Vaheta gripteip, kui see hakkab servadest tõstma, kulub liiga siledale, kleepub ebaühtlaselt, või on märgatavalt rebenenud. Intensiivse kasutuse korral vahetab enamik sõitjaid gripteip iga 2–4 kuu tagant. Märk vajadusest on jalg hakkab talla peal libisema trikkide ajal.'
    },
    {
      q: 'Kas gripteip sobib kõigile tõukeratta talladele?',
      a: 'Tõukeratta gripteip on standardse laiusega (10–11cm) ja lõigatakse vastavalt talla suurusele — sobib enamikule talladele. Paksem gripteip annab rohkem haaret, õhem kulub aeglasemalt. Kõik meie gripteibid on isekleepuvad ja ei vaja lisaliimi.'
    },
    {
      q: 'Miks on gripteip oluline trikitõukse peal?',
      a: 'Gripteip tagab jala kindla haarde talla pinnal — ilma selleta libiseb jalg talla pealt eriti märja ilma, suurte maandumiste ja intensiivsete trikkide ajal. See on ohutuse küsimus. Kulunud gripteip tuleb vahetada kohe, sest slip-trikkide ajal on vigastuste risk kõrgem.'
    },
  ],
};

type PageType =
  | '/toukeratas'
  | '/toukeratas/lenks'
  | '/toukeratas/tald'
  | '/toukeratas/brandid'
  | '/toukeratas/rattad'
  | '/toukeratas/kahvlid'
  | '/toukeratas/klambrid'
  | '/toukeratas/lenksuotsad'
  | '/toukeratas/peegid'
  | '/toukeratas/kaepidemed'
  | '/toukeratas/gripteibid'
  | '/toukeratas/laste-toukeratas';

type SortOption = 'price-asc' | 'price-desc' | 'discount' | 'new';

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState<'initial' | 'lightweight' | 'full' | 'complete'>('initial');
  const { addItem } = useCart();
  const [showNotification, setShowNotification] = useState(false);
  const [notificationProduct, setNotificationProduct] = useState<{name: string; image: string} | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('price-desc');
  
  const [activeFilters, setActiveFilters] = useState({
    productClass: [] as string[],
    categories: [] as string[],
    brands: [] as string[],
    tags: [] as string[],
    colors: [] as string[],
    priceRange: [0, 1000] as [number, number],
    weightRange: [0, 5] as [number, number],
    stockStatus: {
      inStock: true,
      outOfStock: false
    },
    discountStatus: {
      hasDiscount: false,
      noDiscount: true
    }
  });

  // Extract metadata from products for filters
  const { uniqueColors, uniqueBrands, uniqueTags, colorCodes } = extractProductMetadata(products);

  // Helper function to determine the current page type for SEO content
  const getPageType = (): PageType => {
    const pathname = location.pathname;
    if (pathname.includes('/brandid')) {
      return '/toukeratas/brandid';
    } else if (pathname.includes('/laste-toukeratas')) {
      return '/toukeratas/laste-toukeratas';
    } else if (pathname.includes('/gripteibid')) {
      return '/toukeratas/gripteibid';
    } else if (pathname.includes('/lenksuotsad')) {
      return '/toukeratas/lenksuotsad';
    } else if (pathname.includes('/kaepidemed')) {
      return '/toukeratas/kaepidemed';
    } else if (pathname.includes('/klambrid')) {
      return '/toukeratas/klambrid';
    } else if (pathname.includes('/kahvlid')) {
      return '/toukeratas/kahvlid';
    } else if (pathname.includes('/rattad')) {
      return '/toukeratas/rattad';
    } else if (pathname.includes('/peegid')) {
      return '/toukeratas/peegid';
    } else if (pathname.includes('/lenks')) {
      return '/toukeratas/lenks';
    } else if (pathname.includes('/tald')) {
      return '/toukeratas/tald';
    }
    return '/toukeratas';
  };

  // Helper function to parse pathname-based routes (new SEO-friendly URLs)
  const parseFiltersFromPathname = () => {
    const pathname = location.pathname;
    let productClass: string[] = [];
    let categories: string[] = [];
    let sortBy: SortOption = 'price-desc';
    let brands: string[] = [];
    let discountStatus = {
      hasDiscount: false,
      noDiscount: true
    };

    // Check for sort-based paths first (specific paths take precedence)
    if (pathname.includes('/soodus-tooted')) {
      sortBy = 'discount';
      // When on soodus-tooted page, show only products with discounts
      discountStatus = {
        hasDiscount: true,
        noDiscount: false
      };
    } else if (pathname.includes('/uued-tooted')) {
      sortBy = 'new';
    } else if (pathname.includes('/tald')) {
      productClass = ['decks'];
    } else if (pathname.includes('/lenksuotsad')) {
      productClass = ['bar-ends'];
    } else if (pathname.includes('/lenks')) {
      productClass = ['bars'];
    } else if (pathname.includes('/rattad')) {
      productClass = ['wheels'];
    } else if (pathname.includes('/kahvlid')) {
      productClass = ['forks'];
    } else if (pathname.includes('/klambrid')) {
      productClass = ['clamps'];
    } else if (pathname.includes('/peegid')) {
      productClass = ['pegs'];
    } else if (pathname.includes('/kaepidemed')) {
      productClass = ['grips'];
    } else if (pathname.includes('/gripteibid')) {
      productClass = ['griptape'];
    } else if (pathname.includes('/kesktase-toukerattad')) {
      categories = ['Kesktase'];
    } else if (pathname.includes('/pro-tase-toukerattad')) {
      categories = ['Pro tase'];
    } else if (pathname.includes('/street-toukerattad')) {
      categories = ['Street'];
    } else if (pathname.includes('/pargi-toukerattad')) {
      categories = ['Park'];
    } else if (pathname.includes('/laste-toukeratas')) {
      categories = ['Laste'];
    } else if (pathname.includes('/toukeratta-varuosad')) {
      productClass = ['decks', 'bars', 'wheels', 'forks', 'clamps', 'bar-ends', 'pegs', 'grips', 'griptape'];
    } else if (pathname.includes('/brandid/')) {
      const brandMatch = pathname.match(/\/brandid\/([^/]+)/);
      if (brandMatch) {
        brands = [brandMatch[1]];
      }
    }

    return { productClass, categories, sortBy, brands, discountStatus };
  };

  // Helper function to parse URL parameters into filter state
  const parseFiltersFromURL = () => {
    const categoryParam = searchParams.get('category');
    const brandParam = searchParams.get('brand');
    const classParam = searchParams.get('class');
    const searchParam = searchParams.get('search');
    const tagParam = searchParams.get('tag');
    const sortParam = searchParams.get('sortBy');
    const colorsParam = searchParams.get('colors');
    const priceMinParam = searchParams.get('priceMin');
    const priceMaxParam = searchParams.get('priceMax');
    const weightMinParam = searchParams.get('weightMin');
    const weightMaxParam = searchParams.get('weightMax');
    const inStockParam = searchParams.get('inStock');
    const outOfStockParam = searchParams.get('outOfStock');

    // Parse multiple values (comma-separated)
    const parseMultipleValues = (param: string | null) => {
      return param ? param.split(',').filter(Boolean) : [];
    };

    // Get pathname-based filters
    const pathnameFilters = parseFiltersFromPathname();

    return {
      categories: categoryParam ? parseMultipleValues(categoryParam) : pathnameFilters.categories,
      brands: brandParam ? parseMultipleValues(brandParam) : pathnameFilters.brands,
      productClass: classParam ? parseMultipleValues(classParam) : pathnameFilters.productClass,
      tags: parseMultipleValues(tagParam),
      colors: parseMultipleValues(colorsParam),
      priceRange: [
        priceMinParam ? parseInt(priceMinParam) : 0,
        priceMaxParam ? parseInt(priceMaxParam) : 1000
      ] as [number, number],
      weightRange: [
        weightMinParam ? parseFloat(weightMinParam) : 0,
        weightMaxParam ? parseFloat(weightMaxParam) : 5
      ] as [number, number],
      stockStatus: {
        inStock: inStockParam !== 'false',
        outOfStock: outOfStockParam === 'true'
      },
      discountStatus: pathnameFilters.discountStatus,
      searchQuery: searchParam || '',
      sortBy: (sortParam as SortOption) || pathnameFilters.sortBy
    };
  };

  // Helper function to update URL with current filter state
  const updateURLWithFilters = (filters: typeof activeFilters, search: string, sort: SortOption) => {
    const newParams = new URLSearchParams();

    // Add filters to URL if they have values
    if (filters.categories.length > 0) {
      newParams.set('category', filters.categories.join(','));
    }
    if (filters.brands.length > 0) {
      newParams.set('brand', filters.brands.join(','));
    }
    if (filters.productClass.length > 0) {
      newParams.set('class', filters.productClass.join(','));
    }
    if (filters.tags.length > 0) {
      newParams.set('tag', filters.tags.join(','));
    }
    if (filters.colors.length > 0) {
      newParams.set('colors', filters.colors.join(','));
    }
    if (filters.priceRange[0] !== 0) {
      newParams.set('priceMin', filters.priceRange[0].toString());
    }
    if (filters.priceRange[1] !== 1000) {
      newParams.set('priceMax', filters.priceRange[1].toString());
    }
    if (filters.weightRange[0] !== 0) {
      newParams.set('weightMin', filters.weightRange[0].toString());
    }
    if (filters.weightRange[1] !== 5) {
      newParams.set('weightMax', filters.weightRange[1].toString());
    }
    if (!filters.stockStatus.inStock) {
      newParams.set('inStock', 'false');
    }
    if (filters.stockStatus.outOfStock) {
      newParams.set('outOfStock', 'true');
    }
    if (search) {
      newParams.set('search', search);
    }
    if (sort !== 'price-desc') {
      newParams.set('sortBy', sort);
    }

    setSearchParams(newParams);
  };

  // Initialize filters from URL on component mount and when URL changes
  useEffect(() => {
    const urlFilters = parseFiltersFromURL();

    setActiveFilters({
      productClass: urlFilters.productClass,
      categories: urlFilters.categories,
      brands: urlFilters.brands.map(b => b.toLowerCase()),
      tags: urlFilters.tags,
      colors: urlFilters.colors,
      priceRange: urlFilters.priceRange,
      weightRange: urlFilters.weightRange,
      stockStatus: urlFilters.stockStatus,
      discountStatus: urlFilters.discountStatus
    });

    setSearchQuery(urlFilters.searchQuery);
    setSortBy(urlFilters.sortBy);
  }, [searchParams, location.pathname]); // Re-run when URL or pathname changes

  // Create wrapped setters that update both state and URL
  const setActiveFiltersWithURL = (newFilters: typeof activeFilters | ((prev: typeof activeFilters) => typeof activeFilters)) => {
    setActiveFilters(prev => {
      const updatedFilters = typeof newFilters === 'function' ? newFilters(prev) : newFilters;
      // Update URL in next tick to ensure state is updated first
      setTimeout(() => updateURLWithFilters(updatedFilters, searchQuery, sortBy), 0);
      return updatedFilters;
    });
  };

  const setSearchQueryWithURL = (query: string) => {
    setSearchQuery(query);
    setTimeout(() => updateURLWithFilters(activeFilters, query, sortBy), 0);
  };

  const setSortByWithURL = (sort: SortOption) => {
    setSortBy(sort);

    // Navigate to path-based routes for discount and new products
    if (sort === 'discount') {
      navigate('/toukeratas/soodus-tooted');
    } else if (sort === 'new') {
      navigate('/toukeratas/uued-tooted');
    } else {
      // For price sorting, navigate back to base path and use query parameters
      if (sort === 'price-desc') {
        // Default sort - go to base path without query param
        navigate('/toukeratas');
      } else {
        // price-asc or other sorts - use query parameter
        navigate(`/toukeratas?sortBy=${sort}`);
      }
    }
  };

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true);
        setLoadingStage('initial');
        
        // Strategy: Load lightweight data first for fast UI, then upgrade to full data
        console.log('Loading products with smart caching strategy...');
        
        // Step 1: Try to load lightweight products first (faster)
        setLoadingStage('lightweight');
        try {
          const lightweightData = await fetchProductsLight();
          
          if (lightweightData && Array.isArray(lightweightData) && lightweightData.length > 0) {
            console.log('Loaded lightweight products, updating UI...');
            const validLightweightProducts = lightweightData.filter((product: any) => {
              return product && 
                     typeof product === 'object' && 
                     product.variants && 
                     Array.isArray(product.variants) && 
                     product.variants.length > 0;
            });
            
            setProducts(validLightweightProducts);
            setIsLoading(false); // Hide loading immediately after lightweight data is shown
          }
        } catch (lightweightError) {
          console.warn('Lightweight products failed, falling back to full data:', lightweightError);
        }
        
        // Step 2: Load full data in background if needed
        // Only load full data if we don't have fresh lightweight data or if it's been a while
        const needsFullData = shouldRefreshCache() || products.length === 0;
        
        if (needsFullData) {
          try {
            console.log('Loading full product data...');
            const fullData = await fetchProducts();
            
            if (!Array.isArray(fullData)) {
              console.error('Invalid full products data format:', fullData);
              setProducts([]);
              return;
            }
            
            // Additional validation for each product
            const validProducts = fullData.filter((product: any) => {
              if (!product || typeof product !== 'object') {
                console.error('Invalid product entry:', product);
                return false;
              }
              
              if (!product.variants || !Array.isArray(product.variants) || product.variants.length === 0) {
                console.error('Product missing variants:', product.id);
                return false;
              }
              
              // Check if each variant has required properties
              const hasValidVariants = product.variants.every((variant: any) => {
                return variant && variant.id && variant.name && typeof variant.price === 'number';
              });
              
              if (!hasValidVariants) {
                console.error('Product has invalid variants:', product.id);
                return false;
              }
              
              return true;
            });
            
            console.log('Full product data loaded and validated');
            setProducts(validProducts);
          } catch (fullDataError) {
            console.error('Full data loading failed:', fullDataError);
            if (products.length === 0) {
              setProducts([]);
            }
          }
        }
        
        setLoadingStage('complete');
      } catch (error) {
        console.error('Error in product loading strategy:', error);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Preload products when component mounts (for future navigation)
  useEffect(() => {
    // Preload in background for next time
    setTimeout(() => {
      preloadProducts();
    }, 2000); // Wait 2 seconds after initial load
  }, []);

  const handleAddToCart = (variant: ProductVariant) => {
    addItem({
      id: variant.id,
      name: variant.name,
      price: variant.discounted_price || variant.price,
      quantity: 1,
      image: variant.images[0],
      stock: variant.stock // Pass stock to enforce limits
    });

    setNotificationProduct({
      name: variant.name,
      image: variant.images[0]
    });
    setShowNotification(true);

    setTimeout(() => {
      setShowNotification(false);
    }, 5000);
  };

  const resetAllFilters = () => {
    setSearchQuery('');
    setActiveFilters({
      productClass: [],
      categories: [],
      brands: [],
      tags: [],
      colors: [],
      priceRange: [0, 1000],
      weightRange: [0, 5],
      stockStatus: {
        inStock: true,
        outOfStock: false
      },
      discountStatus: {
        hasDiscount: false,
        noDiscount: true
      }
    });
    setSortBy('price-desc');
    
    // Clear URL parameters
    setSearchParams(new URLSearchParams());
  };

  // Apply filters to products
  const filteredProducts = filterProducts(products, activeFilters, searchQuery);

  // Sort the filtered products
  const sortedProducts = sortProducts(filteredProducts, sortBy);

  // Generate dynamic canonical URL including query parameters for better indexing
  const baseUrl = 'https://xn--tuks-0qa.ee';

  // For filter pages, canonicalize to main /toukeratas to consolidate SEO power
  const isFilterPage = location.pathname.includes('/kesktase-toukerattad') ||
                       location.pathname.includes('/pro-tase-toukerattad') ||
                       location.pathname.includes('/street-toukerattad') ||
                       location.pathname.includes('/pargi-toukerattad') ||
                       location.pathname.includes('/soodus-tooted') ||
                       location.pathname.includes('/uued-tooted');

  // Generate canonical URL - exclude query params except for search
  let canonicalUrl = `${baseUrl}${location.pathname}`;

  if (isFilterPage) {
    canonicalUrl = `${baseUrl}/toukeratas`;
  } else if (location.search) {
    // For filter pages with query params, canonicalize to clean version
    canonicalUrl = `${baseUrl}${location.pathname}`;
  }

  // Get current page type for SEO content
  const currentPageType = getPageType();
  const seoData = SEO_CONTENT[currentPageType];

  // Detect brand name for brand pages
  const brandMatch = location.pathname.match(/\/toukeratas\/brandid\/([^/]+)/);
  const currentBrandSlug = brandMatch ? brandMatch[1] : null;

  // Resolve FAQ items — per-path first, then per page-type, then dynamic brand FAQs
  const currentFaqItems: FaqItem[] | null =
    currentPageType === '/toukeratas/brandid' && currentBrandSlug
      ? getBrandFaqs(currentBrandSlug)
      : (FAQ_CONTENT[location.pathname] ?? FAQ_CONTENT[currentPageType] ?? null);

  return (
    <div className="container mx-auto py-12 px-6">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={
          currentPageType === '/toukeratas/lenks' ? 'lenks, lenksud, tõuski lenks, trikitõuks lenks, tõukeratta lenks' :
          currentPageType === '/toukeratas/tald' ? 'tald, tallad, tõuski tald, trikitõuski tald, tõukeratta tald' :
          currentPageType === '/toukeratas/rattad' ? 'tõukeratta rattad, 100mm rattad, 110mm rattad, 120mm rattad, alumiinium ratas, pro scooter wheel' :
          currentPageType === '/toukeratas/kahvlid' ? 'tõukeratta kahvel, IHC kahvel, HIC kahvel, SCS kahvel, scooter fork, alumiinium kahvel' :
          currentPageType === '/toukeratas/klambrid' ? 'tõukeratta klamber, single bolt klamber, double bolt klamber, scooter clamp' :
          currentPageType === '/toukeratas/lenksuotsad' ? 'tõukeratta lenksuotsad, bar ends, scooter bar ends, lenksuots' :
          currentPageType === '/toukeratas/peegid' ? 'tõukeratta peegid, pegs, grinding pegs, scooter pegs, alumiinium peg' :
          currentPageType === '/toukeratas/kaepidemed' ? 'tõukeratta käepidemed, grips, scooter grips, foam grips, gumi käepide' :
          currentPageType === '/toukeratas/gripteibid' ? 'tõukeratta gripteip, griptape, scooter griptape, talla gripteip' :
          currentPageType === '/toukeratas/laste-toukeratas' ? 'laste tõukeratas, tõukeratas lastele, mini tõukeratas, laste trikitõuks, laste scooter, trikitõukeratas lastele, youth scooter' :
          currentPageType === '/toukeratas/toukeratta-varuosad' ? 'tõukeratta varuosad, tõukeratta jupid, tõukeratta tald, tõukeratta lenks, tõukeratta rattad, tõukeratta kahvel, tõukeratta klamber, käepidemed, gripteip' :
          'tõukeratas, tõukerattad, trikitõuks, tõuksid, tõuks, varuosad, scooter, trick scooter'
        } />
        <link rel="canonical" href={canonicalUrl} />
        {/* Add noindex for discount/new pages and any URLs with query parameters */}
        {(location.pathname.includes('/soodus-tooted') ||
          location.pathname.includes('/uued-tooted') ||
          location.search !== '') && (
          <meta name="robots" content="noindex, follow" />
        )}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://xn--tuks-0qa.ee/Touks-icon4.webp" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content="https://xn--tuks-0qa.ee/Touks-icon4.webp" />

        {/* Schema.org Structured Data - For all product listing pages */}
        {(location.pathname === '/toukeratas' ||
          location.pathname === '/toukeratas/tald' ||
          location.pathname === '/toukeratas/lenks' ||
          location.pathname === '/toukeratas/rattad' ||
          location.pathname === '/toukeratas/kahvlid' ||
          location.pathname === '/toukeratas/klambrid' ||
          location.pathname === '/toukeratas/lenksuotsad' ||
          location.pathname === '/toukeratas/peegid' ||
          location.pathname === '/toukeratas/kaepidemed' ||
          location.pathname === '/toukeratas/gripteibid' ||
          location.pathname === '/toukeratas/toukeratta-varuosad' ||
          location.pathname.startsWith('/toukeratas/brandid') ||
          location.pathname === '/toukeratas/kesktase-toukerattad' ||
          location.pathname === '/toukeratas/pro-tase-toukerattad' ||
          location.pathname === '/toukeratas/street-toukerattad' ||
          location.pathname === '/toukeratas/pargi-toukerattad' ||
          location.pathname === '/toukeratas/laste-toukeratas') && (
          <>
            {/* 1. CollectionPage Schema */}
            <script type="application/ld+json">{JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": seoData.title,
              "description": seoData.description,
              "url": canonicalUrl,
              "mainEntity": {
                "@type": "ItemList",
                "itemListElement": sortedProducts.slice(0, 12).map((product, index) => ({
                  "@type": "ListItem",
                  "position": index + 1,
                  "url": `https://xn--tuks-0qa.ee/tooted/${product.id}`
                }))
              }
            })}</script>

            {/* 2. BreadcrumbList Schema - context-aware */}
            <script type="application/ld+json">{JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": (() => {
                const base = [
                  { "@type": "ListItem", "position": 1, "name": "Kodu", "item": "https://xn--tuks-0qa.ee/" },
                  { "@type": "ListItem", "position": 2, "name": "Tõukerattad", "item": "https://xn--tuks-0qa.ee/toukeratas" }
                ];
                const path = location.pathname;
                if (path.includes('/toukeratas/tald')) {
                  return [...base, { "@type": "ListItem", "position": 3, "name": "Tallad", "item": "https://xn--tuks-0qa.ee/toukeratas/tald" }];
                }
                if (path.includes('/toukeratas/lenks')) {
                  return [...base, { "@type": "ListItem", "position": 3, "name": "Lenksud", "item": "https://xn--tuks-0qa.ee/toukeratas/lenks" }];
                }
                if (path.match(/\/toukeratas\/brandid\/([^/]+)/)) {
                  const brandMatch = path.match(/\/toukeratas\/brandid\/([^/]+)/);
                  const brandName = brandMatch ? brandMatch[1].charAt(0).toUpperCase() + brandMatch[1].slice(1) : 'Bränd';
                  return [
                    ...base,
                    { "@type": "ListItem", "position": 3, "name": "Brändid", "item": "https://xn--tuks-0qa.ee/toukeratas/brandid" },
                    { "@type": "ListItem", "position": 4, "name": brandName, "item": `https://xn--tuks-0qa.ee/toukeratas/brandid/${brandMatch?.[1]}` }
                  ];
                }
                if (path.includes('/toukeratas/brandid')) {
                  return [...base, { "@type": "ListItem", "position": 3, "name": "Brändid", "item": "https://xn--tuks-0qa.ee/toukeratas/brandid" }];
                }
                if (path.includes('/kesktase-toukerattad')) {
                  return [...base, { "@type": "ListItem", "position": 3, "name": "Kesktase tõukerattad", "item": "https://xn--tuks-0qa.ee/toukeratas/kesktase-toukerattad" }];
                }
                if (path.includes('/pro-tase-toukerattad')) {
                  return [...base, { "@type": "ListItem", "position": 3, "name": "Pro tase tõukerattad", "item": "https://xn--tuks-0qa.ee/toukeratas/pro-tase-toukerattad" }];
                }
                if (path.includes('/street-toukerattad')) {
                  return [...base, { "@type": "ListItem", "position": 3, "name": "Street tõukerattad", "item": "https://xn--tuks-0qa.ee/toukeratas/street-toukerattad" }];
                }
                if (path.includes('/pargi-toukerattad')) {
                  return [...base, { "@type": "ListItem", "position": 3, "name": "Pargi tõukerattad", "item": "https://xn--tuks-0qa.ee/toukeratas/pargi-toukerattad" }];
                }
                if (path.includes('/laste-toukeratas')) {
                  return [...base, { "@type": "ListItem", "position": 3, "name": "Laste tõukeratas", "item": "https://xn--tuks-0qa.ee/toukeratas/laste-toukeratas" }];
                }
                if (path.includes('/toukeratta-varuosad')) {
                  return [...base, { "@type": "ListItem", "position": 3, "name": "Tõukeratta varuosad", "item": "https://xn--tuks-0qa.ee/toukeratas/toukeratta-varuosad" }];
                }
                return base;
              })()
            })}</script>

            {/* 4. AggregateOffer Schema - Price range */}
            {sortedProducts.length > 0 && (() => {
              const prices = sortedProducts.map(p => p.price).filter(p => p > 0);
              const minPrice = Math.min(...prices);
              const maxPrice = Math.max(...prices);
              return (
                <script type="application/ld+json">{JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "AggregateOffer",
                  "priceCurrency": "EUR",
                  "lowPrice": minPrice.toString(),
                  "highPrice": maxPrice.toString(),
                  "offerCount": sortedProducts.length.toString()
                })}</script>
              );
            })()}

            {/* FAQPage Schema - only for pages with FAQ content */}
            {currentFaqItems && (
              <script type="application/ld+json">{JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": currentFaqItems.map(item => ({
                  "@type": "Question",
                  "name": item.q,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": item.a
                  }
                }))
              })}</script>
            )}

            {/* WebPage Schema with SearchAction */}
            <script type="application/ld+json">{JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": seoData.title,
              "description": seoData.description,
              "url": canonicalUrl,
              "datePublished": "2024-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "isPartOf": {
                "@type": "Website",
                "name": "Tõuks.ee",
                "url": "https://xn--tuks-0qa.ee"
              },
              "potentialAction": {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": `${canonicalUrl}?search={search_term_string}`
                },
                "query-input": "required name=search_term_string"
              }
            })}</script>
          </>
        )}
      </Helmet>
      {showNotification && notificationProduct && (
        <CartNotification
          product={notificationProduct}
          isVisible={showNotification}
          onClose={() => setShowNotification(false)}
        />
      )}

      <ProductFilters
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        activeFilters={activeFilters}
        setActiveFilters={setActiveFiltersWithURL}
        sortBy={sortBy}
        setSortBy={setSortByWithURL}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQueryWithURL}
        uniqueBrands={uniqueBrands}
        uniqueTags={uniqueTags}
        uniqueColors={uniqueColors}
        productClasses={PRODUCT_CLASSES}
        categories={CATEGORIES}
        resetAllFilters={resetAllFilters}
        colorCodes={colorCodes}
        pageType={currentPageType}
        h1Title={seoData.h1Title}
      />

      {/* Show BrandFilter when on /toukeratas/brandid page */}
      {location.pathname === '/toukeratas/brandid' && (
        <div className="mb-8">
          <BrandFilter
            compact
            onBrandSelect={(brand) => {
              setActiveFiltersWithURL(prev => {
                const newBrands = prev.brands.includes(brand)
                  ? prev.brands.filter(b => b !== brand)
                  : [brand];
                return {
                  ...prev,
                  brands: newBrands
                };
              });
            }}
          />
        </div>
      )}

      {/* Show CategoryBanners only on main scooters page */}
      {location.pathname !== '/toukeratas/brandid' && location.pathname !== '/toukeratas/laste-toukeratas' && location.pathname !== '/toukeratas/toukeratta-varuosad' && (
        <>
          <h2 className="text-2xl font-bold mb-6">Leia tõuks taseme või kategooria järgi</h2>
          <CategoryBanners onCategorySelect={(category) => {
            setActiveFiltersWithURL(prev => ({
              ...prev,
              categories: prev.categories.includes(category)
                ? prev.categories.filter(c => c !== category)
                : [category]
            }));
          }} />
        </>
      )}

      {isLoading && loadingStage !== 'complete' && (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4FC1E9] mx-auto mb-4"></div>
            <p className="text-gray-600">
              {loadingStage === 'initial' && 'Laadin tooteid...'}
              {loadingStage === 'lightweight' && 'Laadin põhiandmeid...'}
              {loadingStage === 'full' && 'Täiendan andmeid...'}
            </p>
          </div>
        </div>
      )}

      {/* Mobile "Filtrid" toggle — sits below the category banners */}
      <div className="md:hidden mb-6">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center text-gray-600 hover:text-[#4FC1E9]"
        >
          <Sliders className="w-5 h-5 mr-2" />
          Filtrid
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        <ProductFilterSidebar
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFiltersWithURL}
          sortBy={sortBy}
          setSortBy={setSortByWithURL}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQueryWithURL}
          uniqueBrands={uniqueBrands}
          uniqueTags={uniqueTags}
          uniqueColors={uniqueColors}
          productClasses={PRODUCT_CLASSES}
          categories={CATEGORIES}
          resetAllFilters={resetAllFilters}
          colorCodes={colorCodes}
        />

        <div className="flex-1">
          <div className="flex items-center justify-between mb-6 mt-8">
            <h2 className="text-2xl font-bold">
              {currentPageType === '/toukeratas/toukeratta-varuosad' ? 'Tõukeratta varuosad' :
               currentPageType === '/toukeratas/laste-toukeratas' ? 'Laste tõukerattad' :
               currentPageType === '/toukeratas/rattad' ? 'Tõukeratta rattad' :
               currentPageType === '/toukeratas/kahvlid' ? 'Tõukeratta kahvlid' :
               currentPageType === '/toukeratas/klambrid' ? 'Tõukeratta klambrid' :
               currentPageType === '/toukeratas/lenksuotsad' ? 'Tõukeratta lenksuotsad' :
               currentPageType === '/toukeratas/peegid' ? 'Tõukeratta peegid' :
               currentPageType === '/toukeratas/kaepidemed' ? 'Tõukeratta käepidemed' :
               currentPageType === '/toukeratas/gripteibid' ? 'Tõukeratta gripteibid' :
               currentPageType === '/toukeratas/tald' ? 'Tõukeratta tallad' :
               currentPageType === '/toukeratas/lenks' ? 'Tõukeratta lenksud' :
               'Tõukerattad ja varuosad'}
            </h2>
            {(location.search !== '' || searchQuery !== '') && (
              <button
                onClick={resetAllFilters}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#4FC1E9] transition-colors duration-200 border border-gray-200 hover:border-[#4FC1E9] rounded-full px-3 py-1.5"
              >
                <X className="w-3.5 h-3.5" />
                Tühista filtrid
              </button>
            )}
          </div>
          <ProductGrid
            products={sortedProducts}
            onAddToCart={handleAddToCart}
            resetAllFilters={resetAllFilters}
          />

          {/* Short SEO Description - moved below products, above FAQ */}
          <div className="bg-gradient-to-r from-[#4FC1E9]/5 to-transparent rounded-lg p-6 mt-16">
            <p className="text-gray-700 leading-relaxed text-sm md:text-base">{seoData.shortDescription}</p>
          </div>

          {/* Long SEO Description - below products only */}
          <div className="mt-8">
            <p className="text-gray-700 leading-relaxed text-sm md:text-base whitespace-pre-wrap">{seoData.longDescription}</p>
          </div>
        </div>
      </div>

      {/* FAQ Section — shown only for pages that have FAQ content */}
      {currentFaqItems && (
        <section className="py-12 md:py-20 mt-4" id="korduma-kippuvad-kusimused">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Korduma kippuvad küsimused</h2>
            <p className="text-center text-gray-500 mb-10">Leidsid vastused kiirelt — kui ei, siis kirjuta meile!</p>
            <div className="space-y-3">
              {currentFaqItems.map((item, i) => (
                <div key={i} className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left font-semibold text-gray-800 hover:text-[#4FC1E9] transition-colors duration-200"
                    aria-expanded={openFaq === i}
                  >
                    <span>{item.q}</span>
                    <ChevronDown
                      className={`w-5 h-5 flex-shrink-0 ml-4 text-[#4FC1E9] transition-transform duration-300 ${
                        openFaq === i ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === i ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <p className="px-6 pb-5 text-gray-600 leading-relaxed">{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
